import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from '@tanstack/react-query';
import axios from 'axios';
import mime from 'mime';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import { DropDown } from '../../components/common';
import SwitchBar from '../../components/switch-bar';
import FilterBar from '../peer-listing/components/filter-bar';
import { FilterOptionsType } from './common/review-page-controls';
import { getListenerProfileUrl, HeaderOptions } from './common/listener-review-constants';
import { useUpdateTag } from '../../hooks/useListenerTags';
import {
  BackgroundTag,
  ListenerRole,
  ListenerRoleResponse,
  PeerOptions,
  TagStatus,
  usePeers,
} from '../../hooks/usePeers';

const BackgroundTagApproval: React.FC = (): JSX.Element => {
  const [filters, setFilters] = useState<PeerOptions>({
    limit: 25,
    page: 1,
    hasBackgroundTags: true,
  });
  const { limit, page, backgroundTagApproved, ...rest } = filters;
  const {
    data: peers,
    isLoading,
    refetch,
  } = usePeers({
    limit,
    page,
    hasBackgroundTags: true,
    backgroundTagApproved: backgroundTagApproved,
    ...rest,
  });

  const backgroundTagCount = (backgroundTags: BackgroundTag[], filterValue: boolean | undefined) => {
    if (filterValue === true) {
      return backgroundTags?.filter((tag) => tag.status === 'APPROVED');
    } else if (filterValue === false) {
      return backgroundTags?.filter((tag) => tag.status === 'UNGRADED');
    } else {
      return backgroundTags;
    }
  };

  const totalTags = peers?.data?.reduce(
    (acc, peer) => acc + (backgroundTagCount(peer?.background_tags, filters.backgroundTagApproved).length ?? 0),
    0,
  );

  return (
    <>
      <Header heading={'Background Tags'} />
      <SwitchBar heading={HeaderOptions} position={5} />
      <FilterBar count={totalTags} setFilters={setFilters} filters={filters} />
      {isLoading && <Spinner alignmentClass={'left-64 top-40 fixed z-100 inset-0 overflow-y-auto'} />}
      <BackgroundTagBody
        peers={peers?.data}
        filters={filters}
        setFilters={setFilters}
        backgroundTagCount={backgroundTagCount}
        refetch={refetch}
      />
    </>
  );
};

interface BackgroundTagBodyProps {
  peers: ListenerRole[] | undefined;
  filters: PeerOptions;
  setFilters: React.Dispatch<React.SetStateAction<PeerOptions>>;
  backgroundTagCount: (backgroundTags: BackgroundTag[], filterValue: boolean | undefined) => BackgroundTag[];
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<ListenerRoleResponse, unknown>>;
}

const BackgroundTagBody: React.FC<BackgroundTagBodyProps> = ({
  peers,
  filters,
  setFilters,
  backgroundTagCount,
  refetch,
}) => {
  const [filterOptions] = useState<FilterOptionsType>({
    all: 'All',
    approved: 'Approved',
    pending: 'Pending',
  });

  const checkApprovalFilter = (filterValue: boolean | undefined) => {
    if (filterValue === undefined) {
      return 'all';
    } else if (filterValue === true) {
      return 'approved';
    } else if (filterValue === false) {
      return 'pending';
    }
  };

  return (
    <div className="max-window-height-connections overflow-y-auto px-7 font-['Montserrat']">
      <table className="table-row-format fixed relative sticky top-0 z-50 mb-2 w-full table-fixed border-collapse bg-white">
        <thead className="h-full bg-white">
          <tr className="w-full px-7 text-left">
            <th className="w-4/124 sticky top-0 mb-4 h-full bg-white text-left">Peer</th>
            <th className="sticky top-0 mb-4 h-full w-3/12 bg-white text-left">Proof</th>
            <th className="sticky top-0 mb-4 h-full w-5/12 bg-white text-left">
              <div className="flex items-center justify-between">
                <p>Tag</p>
                <div className="flex justify-end">
                  <div className="gray-background-dark flex items-center justify-between py-4">
                    {Object.keys(filterOptions).map((item: string) => (
                      <button
                        key={item}
                        className={
                          'px-5 py-1 text-sm ' +
                          (item === checkApprovalFilter(filters.backgroundTagApproved)
                            ? 'bg-blue-primary text-white'
                            : 'bg-gray-background-dark text-gray-dark')
                        }
                        onClick={() => {
                          setFilters((prevFilters) => ({
                            ...prevFilters,
                            backgroundTagApproved: item === 'all' ? undefined : item === 'approved' ? true : false,
                          }));
                        }}
                      >
                        {filterOptions[item]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </th>
          </tr>
        </thead>
      </table>
      {peers &&
        peers.length > 0 &&
        peers.map((peer: ListenerRole) => {
          return (
            <>
              {backgroundTagCount(peer?.background_tags, filters.backgroundTagApproved).map((tag) => {
                return <BackgroundTagRow peer={peer} tag={tag} filters={filters} refetch={refetch} />;
              })}
            </>
          );
        })}
    </div>
  );
};

interface BackgroundTagRowProps {
  peer: ListenerRole;
  tag: BackgroundTag;
  filters: PeerOptions;
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<ListenerRoleResponse, unknown>>;
}

const BackgroundTagRow: React.FC<BackgroundTagRowProps> = ({ peer, tag, filters, refetch }) => {
  const history = useHistory();
  const dateRequested = new Date(tag?.created_at).toDateString();
  const { updateTag } = useUpdateTag(peer.id, tag.id);
  const [enableSave, setEnableSave] = useState<boolean>(false);
  const [tagApprovedStatus, setApprovalStatus] = useState<TagStatus>(tag.status);

  const getUrlFileType = (url: string) => {
    const newUrl = new URL(url);
    const ext: any = newUrl.pathname.split('.').pop();
    return ext === '/' ? undefined : mime?.getType(ext.toLowerCase());
  };

  const handleImage = async (file_url: string, key: string) => {
    if (key === 'VERIFICATION') {
      const authenticateToken = localStorage.getItem('authorization_token');
      const response: any = await axios.get(file_url, {
        responseType: 'arraybuffer',
        headers: {
          Authorization: 'Bearer ' + authenticateToken,
          'X-Requested-With': 'XMLHTTPRequest',
          Accept: 'application/json',
        },
      });
      const blob: Blob = new Blob([response.data], {
        type: getUrlFileType(file_url) || undefined,
      });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } else {
      window.open(file_url, '_blank');
    }
  };

  const handleSave = (tagApprovedStatus: TagStatus) => {
    const payload = {
      tag_id: tag.tag_id,
      status: tagApprovedStatus,
    };
    updateTag.mutate(payload);
    setEnableSave(false);
    refetch();
  };

  return (
    <div className="text-gray-dark flex h-36 w-full space-x-2 space-y-2 rounded border-b-8 border-white bg-gray-200 text-left">
      <div className="w-4/12">
        <div className="flex h-full w-full items-center pl-4">
          <div className="flex h-full w-full flex-col space-y-1 truncate pl-6 pt-4 font-['Montserrat'] font-bold text-black">
            {peer?.user?.first_name || peer?.user?.last_name ? (
              <p className="text-base">
                {peer.user.first_name} {peer.user.last_name}
              </p>
            ) : (
              <p className="text-base">No name on file</p>
            )}
            <p>
              {peer.user.display_name ? <span className="text-sm">aka </span> : ''}
              <a
                className={`${peer.user.display_name ? `text-base` : `text-sm italic`} cursor-pointer underline`}
                href={getListenerProfileUrl(peer.id, peer.user.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {peer.user.display_name ? peer.user.display_name : 'No display name'}
              </a>
            </p>
            <>
              <p>{peer?.user?.email_address}</p>
            </>
            <div className="flex w-3/4 justify-between space-x-2 text-xs text-neutral-600">
              <p>User ID: #{peer.user.id}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="w-3/12">
        <div className="col-span-3 mt-11 flex w-full flex-col justify-center">
          <>
            {tag?.media ? (
              tag?.media?.map((media) => {
                return (
                  <div>
                    <button
                      className="w-full overflow-hidden text-ellipsis whitespace-nowrap text-left"
                      onClick={() => handleImage(media?.file_url, media?.file_key)}
                    >
                      <p className="cursor-pointer truncate underline">
                        {media?.file_name ? media?.file_name : media?.file_url}
                      </p>
                    </button>
                  </div>
                );
              })
            ) : (
              <p>No File Found </p>
            )}
          </>
        </div>
      </div>
      <div className="w-4/12">
        <div className="flex w-full flex-col space-y-3 truncate font-['Montserrat'] font-normal">
          <div
            className={`${
              tag?.status === 'APPROVED' ? 'bg-citrus' : 'stacked-bar-orange-primary'
            } w-max rounded-md text-sm font-bold text-white`}
          >
            <div className="flex px-3 py-2">
              <div className="grow pr-2">{tag.tag.name}</div>
            </div>
          </div>
          <>
            <p className="text-xs">Submitted on: {dateRequested}</p>
          </>
          <div className="-z-10 w-1/2 min-w-max">
            <DropDown
              Label={''}
              value={tagApprovedStatus}
              setValue={(value) => {
                setApprovalStatus(value);
                setEnableSave(true);
              }}
            >
              {tag.status === 'APPROVED' ? (
                <>
                  <option value="APPROVED">Approved</option>
                  <option value="UNGRADED">Needs Approval</option>
                  <option value="REJECTED">Reject</option>
                </>
              ) : tag.status === 'REJECTED' ? (
                <>
                  <option value="REJECTED">Rejected</option>
                  <option value="UNGRADED">Needs Approval</option>
                  <option value="APPROVED">Approve</option>
                </>
              ) : (
                <>
                  <option value="UNGRADED">Needs Approval</option>
                  <option value="APPROVED">Approve</option>
                  <option value="REJECTED">Reject</option>
                </>
              )}
            </DropDown>
          </div>
        </div>
      </div>
      <div className="flex w-1/12 items-center pb-6">
        <button
          type="button"
          className={`${
            enableSave ? 'bg-bright-blue text-white' : 'bg-gray-background-dark text-gray-dark'
          } w-auto rounded-lg px-3 py-1`}
          onClick={() => {
            handleSave(tagApprovedStatus);
          }}
          disabled={!enableSave}
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default BackgroundTagApproval;
